import { z } from 'zod';
import {
  ExecutionTypeSchema,
  type ForbiddenAction,
  ForbiddenActionSchema,
  type PermittedTool,
  PermittedToolSchema,
  WorkerStageSchema,
} from './enums';
import { QaReviewStateSchema } from './finding';
import { NonEmptyStringSchema } from './primitives';

/**
 * The worker contract (DESIGN_SPEC §6.1, CLAUDE.md §Core conventions).
 *
 * Workers are constrained functions, not chat assistants. A run is rejected if
 * its output fails schema validation, references ids absent from the canonical
 * model, or attempts a forbidden action.
 *
 * `forbidden_actions` is a closed enum rather than free text specifically so
 * `assertNotForbidden` can enforce it. A prose restriction nobody can check is
 * documentation; this is a control.
 *
 * **`permitted_tools` is closed for the same reason, since sds-08.** The
 * argument was always available and only bit on one list, because until the
 * model router there was no tool anything could be refused: every worker was
 * deterministic and this array was free text nothing read. It is now the
 * positive half of the pair the router checks — `forbidden_actions` says a
 * worker may not call out, `permitted_tools` says which call it may make
 * (docs/DECISIONS.md ZD-046).
 */
export const WorkerContractSchema = z.object({
  worker_name: NonEmptyStringSchema,
  execution_type: ExecutionTypeSchema,
  permitted_stages: z.array(WorkerStageSchema).min(1),
  input_schema_ref: NonEmptyStringSchema,
  output_schema_ref: NonEmptyStringSchema,
  permitted_tools: z.array(PermittedToolSchema),
  forbidden_actions: z.array(ForbiddenActionSchema),
  evidence_requirements: z.array(z.string()),
  confidence_required: z.boolean(),

  /** Fixed for every worker. Cross-tenant reach is not a configurable option. */
  tenant_boundary: z.literal('assessment_tenant_only'),

  /** Every run writes an Agent Run record. Also not optional. */
  audit: z.object({ writes_agent_run: z.literal(true) }),
});
export type WorkerContract = z.infer<typeof WorkerContractSchema>;

export class ForbiddenActionError extends Error {
  constructor(
    readonly worker_name: string,
    readonly action: ForbiddenAction,
  ) {
    super(`Worker ${worker_name} attempted forbidden action: ${action}`);
    this.name = 'ForbiddenActionError';
  }
}

export class StageViolationError extends Error {
  constructor(
    readonly worker_name: string,
    readonly stage: string,
  ) {
    super(`Worker ${worker_name} is not permitted to act in stage: ${stage}`);
    this.name = 'StageViolationError';
  }
}

export class ToolNotPermittedError extends Error {
  constructor(
    readonly worker_name: string,
    readonly tool: PermittedTool,
  ) {
    super(`Worker ${worker_name} is not permitted to use tool: ${tool}`);
    this.name = 'ToolNotPermittedError';
  }
}

export function isForbidden(contract: WorkerContract, action: ForbiddenAction): boolean {
  return contract.forbidden_actions.includes(action);
}

export function assertNotForbidden(contract: WorkerContract, action: ForbiddenAction): void {
  if (isForbidden(contract, action)) {
    throw new ForbiddenActionError(contract.worker_name, action);
  }
}

export function assertPermittedStage(
  contract: WorkerContract,
  stage: z.infer<typeof WorkerStageSchema>,
): void {
  if (!contract.permitted_stages.includes(stage)) {
    throw new StageViolationError(contract.worker_name, stage);
  }
}

/**
 * The positive half of the pair. `assertNotForbidden` refuses a *class* of act;
 * this refuses the specific tool, so a worker permitted to call out at all is
 * still only permitted the calls its contract names.
 *
 * Both run on every model call, and neither can be skipped, because the router
 * takes the `WorkerContract` itself rather than a worker name — there is no
 * signature that reaches a provider without passing the thing these read
 * (docs/DECISIONS.md ZD-046). That is ZD-007's posture: the bypass is
 * unrepresentable rather than merely un-called.
 */
export function assertPermittedTool(contract: WorkerContract, tool: PermittedTool): void {
  if (!contract.permitted_tools.includes(tool)) {
    throw new ToolNotPermittedError(contract.worker_name, tool);
  }
}

/**
 * The ten check names DESIGN_SPEC §6.5 declares, kept whole (ZD-080 §1).
 *
 * **The list is the vocabulary and `QA_CHECK_DISPOSITIONS` is the answer.**
 * Deleting the names that turned out to have no work here would lose the
 * measurement that says why, and the next session reading §6.5 would have to
 * take it again. ZD-079's own posture, one object along: a stage nothing can
 * pass is a claim and was deleted; a *name* carrying a recorded reason is a
 * record and stays.
 */
export const QA_CHECK_NAMES = [
  'grounding',
  'duplication',
  'severity_consistency',
  'domain_fit',
  'assumption_clarity',
  'recommendation_quality',
  'control_mapping_validity',
  'privacy_language',
  'ai_scope',
  'ot_safety',
] as const;
export const QaCheckNameSchema = z.enum(QA_CHECK_NAMES);
export type QaCheckName = z.infer<typeof QaCheckNameSchema>;

/** Where a §6.5 check actually landed. */
export const QA_CHECK_DISPOSITIONS_KINDS = [
  /** This stage runs it, and it can fail. */
  'runs_here',
  /**
   * A producer already enforces it, **earlier**. Re-running it here would be
   * ZD-048 §7's masking twin: breaking either copy changes no behaviour, so the
   * mutation reports `STAYED GREEN` while the guard reads as a protection.
   */
  'settled_upstream',
  /**
   * The failure it names is real and happens at a layer this stage cannot see,
   * so a verdict here would be green for the wrong reason.
   */
  'unreachable_at_this_layer',
  /** Nothing it would be about exists. */
  'no_subject',
] as const;
export type QaCheckDispositionKind = (typeof QA_CHECK_DISPOSITIONS_KINDS)[number];

export interface QaCheckDisposition {
  kind: QaCheckDispositionKind;
  /** What settles it, or why nothing can — measured, not asserted. */
  why: string;
}

/**
 * The count `sds-05u` took before writing this worker (ZD-080 §1).
 *
 * Six of §6.5's eight `pass|fail` checks have a producer and **every one of the
 * six is upstream of this stage**, so the honest output is not ten unconditional
 * verdicts — that is the padding ZD-060 refused and ZD-079 deleted a stage over.
 * The table is here rather than only in the decision entry because this is the
 * file a session reaching for a QA check will open (ZD-046 §7's rule).
 */
export const QA_CHECK_DISPOSITIONS: Record<QaCheckName, QaCheckDisposition> = {
  grounding: {
    kind: 'settled_upstream',
    why:
      '`validateFinding` rejects on eight grounding codes at the **analysis** stage, three ' +
      'transitions earlier, and a refused draft is never persisted — 0 rejections against 102 ' +
      'accepted on the corpus, and 0 by construction. DESIGN_SPEC §11.1 already says so in its ' +
      'own prose: "Grounding is verified by validateFinding, in code, before a human sees ' +
      'anything."',
  },
  duplication: {
    kind: 'unreachable_at_this_layer',
    why:
      'Nothing compares two findings, and nothing here could. `analyseWithPacks` emits one ' +
      'match per (pattern, subject) over a de-duplicated subject list, so the 102 corpus ' +
      'findings carry 102 distinct reproducibility keys **by construction**. The duplication ' +
      'that exists is one layer up: `identityKey` is a normalised name, so the 15 of the 60 ' +
      '`same` pairs the oracle labels that it does not join stay separate objects, and the ' +
      'normalised models carry ' +
      '0 duplicated (type, name) pairs — which is the definition of what the key already caught ' +
      'rather than evidence of none. ZD-047 §5: no check can catch a wrong resolution.',
  },
  severity_consistency: {
    kind: 'settled_upstream',
    why:
      'Two producers, both earlier. `qrsMateriality` is pure over the persisted ' +
      '`RiskDimensions`, so the band is recomputable — 0 mismatches on 102, and 0 by ' +
      'construction, since `validateFinding` writes it from the same `Scorer` and ' +
      '`ApproverDecisionSchema` refuses a band overwrite. The honesty half, ' +
      '`scoringDetailNamesItsGaps`, was the `scoring_complete` guard until ZD-093 deleted that ' +
      'stage, and is now a per-scorer conformance test — the settlement is unchanged and only ' +
      'its second citation moved (ZD-058 §6).',
  },
  domain_fit: {
    kind: 'settled_upstream',
    why:
      '`validateFinding`’s `domain_pack_mismatch`, and it is unfalsifiable in the production ' +
      'path: `matchFrom` sets `domain: resolved.domain`, `renderDraft` passes it through, and ' +
      'the check compares it to `resolved.domain` — the same value on both sides. 0 of 102, ' +
      'with no input that could differ (ZD-052 §5: a check on output is evidence about the ' +
      'producer only where the producer had a choice).',
  },
  assumption_clarity: {
    kind: 'settled_upstream',
    why:
      'Schema-enforced earlier, and its own named population is empty by design. ' +
      '`ConditionalSchema` refuses a conditional finding with no stated condition — 50 ' +
      'conditional, 0 without one. And there are **0 `Assumption` objects** across the four ' +
      'corpus models, 0 of 342 witness-attribute reads carrying the `assumed` basis, and 0 ' +
      'findings with a `depends_on_assumption_id`; `packages/corpus/README.md` says why, ' +
      'deliberately, and warns that a metric on it reports perfect behaviour for an extractor ' +
      'never asked to make one.',
  },
  recommendation_quality: {
    kind: 'no_subject',
    why:
      '`recommendations_created` and the `Recommendation` worker were refused on measurement, ' +
      'three times (ZD-060, ZD-062, ZD-079). `RecommendationSchema` survives with no producer, ' +
      'so a verdict here would be `pass` over an empty set for ever.',
  },
  control_mapping_validity: {
    kind: 'settled_upstream',
    why:
      'A build-time invariant plus a transition guard. `buildControlMappings`’ two throws are ' +
      'unreachable on any pinned pack — the pattern comes from the index the finding anchored ' +
      'to, and `control-references-resolve.test.ts` asserts every referenced `qrsctl_*` ' +
      'resolves in the 36-control catalogue (181 rows, 0 throws). The runtime property is ' +
      '`control_mapping_complete`’s own `allPresent`.',
  },
  privacy_language: {
    kind: 'runs_here',
    why:
      'The one check with work in it, and the work was a **caller** rather than a rule. ' +
      '`refuseLegalConclusion` had two production callers: `acceptSentences`, over ' +
      'model-authored slots only, and `refuseConclusions`, over report-authored text that does ' +
      'not exist yet at this stage. Neither sees the finding prose and pattern labels a client ' +
      'reads — 868 strings across 102 findings and 154 clarifications, of which 6 distinct ' +
      'pattern titles reach a client **only** through a clarification. 0 hits today: uncaught ' +
      'rather than unfirable (ZD-055).',
  },
  ai_scope: {
    kind: 'settled_upstream',
    why:
      '§6.5 pins this `"n/a"` and that is stale — AI ships in MVP and fires on **7 of 102** ' +
      'corpus findings (76 it, 19 privacy). It is not a separate check, though: which pack may ' +
      'claim a finding is `domain_fit`, and what is in the AI domain’s scope is the AI pack’s ' +
      'own predicates. Folded rather than added.',
  },
  ot_safety: {
    kind: 'no_subject',
    why:
      'OT is a reserved domain (CLAUDE.md §MVP scope): vocabulary exists, no pack does. §6.5’s ' +
      '`"n/a"` is correct here and stays correct until a pack lands.',
  },
};

/**
 * The checks this stage runs, **derived** from the table above.
 *
 * Not a second list beside it: two expressions of one rule cannot be compared,
 * so nothing could see them disagree (ZD-072 §3, ZD-017).
 */
export const QA_CHECKS_RUN_HERE: readonly QaCheckName[] = QA_CHECK_NAMES.filter(
  (name) => QA_CHECK_DISPOSITIONS[name].kind === 'runs_here',
);

export class QaCheckDoesNotRunHereError extends Error {
  constructor(readonly check: QaCheckName) {
    super(
      `${check} may not carry a QA flag: its disposition is ` +
        `"${QA_CHECK_DISPOSITIONS[check].kind}", so a verdict from this stage would be a claim ` +
        `about work it did not do`,
    );
    this.name = 'QaCheckDoesNotRunHereError';
  }
}

/**
 * One thing the QA review refused, on one finding.
 *
 * `check` is narrowed to `QA_CHECKS_RUN_HERE` **at the schema** rather than by
 * the worker remembering to. A flag naming `grounding` would be this stage
 * claiming credit for `validateFinding`'s work three transitions earlier, and
 * an evidence pack cannot tell a re-run from a run.
 */
export const QaFlagSchema = z
  .object({
    finding_id: NonEmptyStringSchema,
    check: QaCheckNameSchema,
    /** What was refused, in words — never a count (ZD-043 §8). */
    detail: NonEmptyStringSchema,
  })
  .superRefine((value, ctx) => {
    if (!QA_CHECKS_RUN_HERE.includes(value.check)) {
      ctx.addIssue({
        code: 'custom',
        message: new QaCheckDoesNotRunHereError(value.check).message,
        path: ['check'],
      });
    }
  });
export type QaFlag = z.infer<typeof QaFlagSchema>;

/**
 * What the automated QA review returns (DESIGN_SPEC §6.5, ZD-080).
 *
 * **Three of §6.5's four fields changed, each on a measurement.**
 *
 * `checks` was ten unconditional `pass|fail` keys; six of the eight are settled
 * upstream, so writing `pass` for them would be a record of work that did not
 * happen — ZD-079 §9's argument about an audit event, arriving in an output
 * shape instead of a state. `QA_CHECK_DISPOSITIONS` carries the answer and
 * `flags` carries the failures.
 *
 * `review_decision` is **gone**. Of its three members `approved` is a forbidden
 * action on this contract and §6.5's own closing sentence says so; `rejected` is
 * the human's, per finding, not the stage's; which leaves one value, derivable
 * from `flags.length` — a stored figure our own code computes (ZD-065).
 *
 * `required_changes` becomes `flags`, which names its members rather than
 * counting them.
 *
 * `reviewed_finding_ids` becomes `finding_states`, because the stage's whole
 * reason to exist is that it is the only producer of `review_required` and
 * `qa_flagged` (see `QA_REVIEW_STATES`) — a list of ids reviewed does not say
 * which state each was left in, and the `qa_review_completed` guard reads the
 * state.
 */
export const QaReviewResultSchema = z.object({
  finding_states: z.record(NonEmptyStringSchema, QaReviewStateSchema),
  flags: z.array(QaFlagSchema),
  /** The strings this run actually walked — the denominator behind 0 flags. */
  strings_checked: z.number().int().nonnegative(),
});
export type QaReviewResult = z.infer<typeof QaReviewResultSchema>;
